<template>
    <div v-if="message" :class="alertClass">
        {{ message }}
    </div>

    <!-- Show loading spinner when data is loading -->
    <div v-if="loading" class="d-flex justify-content-center align-items-center" style="height: 100px;">
        <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">{{ $t('loading') }}</span>
        </div>

    </div>
    <div v-else-if="transformedData.length === 0" class="alert alert-info">
        {{ $t('no_data') }}
    </div>
    <div v-else>
        <div class="row mb-1 mt-4">
            <label class="mb-3">
                <input type="checkbox" v-model="showTempSensors" />
                {{ $t('show_temp_sensors') }}
            </label>
        </div>

        <div class="table-responsive px-3 px-md-4 px-lg-5">
            <DataTable :data="transformedData" :columns="dataHeaders" :options="options" @click="openChildTable"
                ref="datatable" class="table table-striped" />
        </div>


        <TempGraph :is-admin="isAdmin" :show-graph="showTempGraph" :device-name="devicename"
            @close-popup="clearPopups" />
        <HumGraph :is-admin="isAdmin" :show-graph="showHumGraph" :device-name="devicename" @close-popup="clearPopups" />
        <EventLog :show-event-log="showEventLog" :device-name="devicename" @close-popup="clearPopups" />
    </div>

</template>

<script setup>
import { fetchData, fetchDataInBatches } from '@/helpers';
import DataTable from 'datatables.net-vue3';
import DataTablesCore from 'datatables.net';
import DataTablesBS5 from 'datatables.net-bs5';

DataTable.use(DataTablesCore);
DataTable.use(DataTablesBS5);
const options = {
    paging: true,
    searching: true,
    ordering: true,
    // Default order: favorites first, then group_name ascending
    order: [[0, 'desc'], [1, 'asc']],
    // make responsive on small screens
    responsive: true,
    // let the table auto-fit into the page instead of forcing a horizontal scroller
    scrollX: false,
    // do not let DataTables calculate column widths (use CSS to control sizing)
    autoWidth: false,
    // Ensure favorites are always applied as the primary ordering key even when the user sorts other columns
    orderFixed: { pre: [[0, 'desc']] },
    language: {
        url: 'https://cdn.datatables.net/plug-ins/2.3.2/i18n/fi.json'
    }
};
</script>

<script>
import { defineAsyncComponent } from 'vue';
const { getCookie, formattedTime } = require('@/helpers');

// Lazy-load popup/graph components (only loaded when opened by user)
const TempGraph = defineAsyncComponent(() =>
    import(/* webpackChunkName: "graphs" */ './TempGraph.vue')
);
const HumGraph = defineAsyncComponent(() =>
    import(/* webpackChunkName: "graphs" */ './HumGraph.vue')
);
const EventLog = defineAsyncComponent(() =>
    import(/* webpackChunkName: "eventlog" */ './EventLog.vue')
);

export default {
    components: {
        TempGraph,
        HumGraph,
        EventLog,
        DataTable
    },
    props: {
        isAdmin: {
            type: Boolean,
            required: true,
            default: false
        },
        groups: {
            type: Array,
            required: true,
            default: () => []
        }
    },
    data() {
        return {
            data: [],
            loading: true,
            showTempGraph: false,
            showHumGraph: false,
            showEventLog: false,
            devicename: '',
            alertClass: '',
            message: '',
            showTempSensors: false
        };
    },
    watch: {
        groups: {
            immediate: true,
            handler(newGroups) {
                if (newGroups && newGroups.length > 0) {
                    this.initialize();
                }
            }
        }
    },
    computed: {
        dataHeaders() {
            const dataHeaders = [];
            dataHeaders.push(
                { data: 'favorite', title: this.$t('dataheaders.favorite'), render: (data, type, row) => this.renderColumn(data, type, row, 'favorite') },
                { data: 'group_name', title: this.$t('dataheaders.group_name') },
                { data: 'address', title: this.$t('dataheaders.address') },
                { data: 'device_nickname', title: this.$t('dataheaders.device_nickname') })
            if (this.isAdmin) {
                dataHeaders.push({ data: 'device_name', title: this.$t('dataheaders.device_name') })
            }
            dataHeaders.push(
                { data: 'last_online', title: this.$t('dataheaders.last_online'), render: (data, type, row) => this.renderColumn(data, type, row, 'last_online') },
                { data: 'battery', title: this.$t('dataheaders.battery'), render: (data, type, row) => this.renderColumn(data, type, row, 'battery') },
                { data: 'smoke_sensor', title: this.$t('dataheaders.smoke_sensor'), render: (data, type, row) => this.renderColumn(data, type, row, 'smoke_sensor') },
                { data: 'temp_sensor', title: this.$t('dataheaders.temp_sensor'), render: (data, type, row) => this.renderColumn(data, type, row, 'temp_sensor') },
                { data: 'temperature', title: this.$t('dataheaders.temperature'), render: (data, type, row) => this.renderColumn(data, type, row, 'temperature') },
                { data: 'humidity', title: this.$t('dataheaders.humidity'), render: (data, type, row) => this.renderColumn(data, type, row, 'humidity') },
                { data: 'removed', title: this.$t('dataheaders.removed'), render: (data, type, row) => this.renderColumn(data, type, row, 'removed') },
                {
                    data: null,
                    className: 'dt-control',
                    orderable: false,
                    defaultContent: '',
                    title: this.$t('dataheaders.additional_info'),
                    render: (data, type, row) => this.renderColumn(data, type, row, 'additional_info')
                })
            return dataHeaders;
        },
        transformedData() {
            if (this.loading || !this.data) return [];

            if (!this.showTempSensors) {
                // Hide devices with device_name not starting with ORIGIN
                const filteredData = this.data.filter(item => item.device_name.startsWith('ORIGIN'));
                return filteredData.map(item => ({
                    favorite: item.favorite,
                    last_online: item.last_online,
                    removed: item.removed,
                    group_name: this.getGroupName(item.group_id),
                    address: this.getGroupAddress(item.group_id),
                    device_nickname: item.device_nickname,
                    device_name: item.device_name,
                    battery: item.battery,
                    smoke_sensor: item.smoke_sensor,
                    temp_sensor: item.temp_sensor,
                    temperature: item.temperature,
                    humidity: item.humidity,
                    assignees: item.assignees,
                    device_id: item.device_id
                }));
            } else {
                return this.data.map(item => ({
                    favorite: item.favorite,
                    last_online: item.last_online,
                    removed: item.removed,
                    group_name: this.getGroupName(item.group_id),
                    address: this.getGroupAddress(item.group_id),
                    device_nickname: item.device_nickname,
                    device_name: item.device_name,
                    battery: item.battery,
                    smoke_sensor: item.smoke_sensor,
                    temp_sensor: item.temp_sensor,
                    temperature: item.temperature,
                    humidity: item.humidity,
                    assignees: item.assignees,
                    device_id: item.device_id
                }));
            }


        }
    },
    methods: {
        async initialize() {
            try {
                this.loading = true;
                this.data = await fetchData.call(this, 'data');
                
                // Skip fetch if no devices
                if (!this.data || this.data.length === 0) {
                    this.loading = false;
                    return;
                }

                const deviceIds = this.data.map(d => d.device_id);

                const [tempData, humData, favorites] = await Promise.all([
                    fetchDataInBatches.call(this, 'temperature', 'device_ids', deviceIds),
                    fetchDataInBatches.call(this, 'humidity', 'device_ids', deviceIds),
                    fetchData.call(this, 'favorites')
                ]);

                this.data.forEach(item => {
                    const temp = tempData.find(t => t.device_id === item.device_id);
                    item.temperature = temp ? temp.temperature : 'N/A';
                });

                this.data.forEach(item => {
                    const hum = humData.find(h => h.device_id === item.device_id);
                    item.humidity = hum
                        ? (hum.humidity === null && hum.hum_avg !== null
                            ? hum.hum_avg
                            : hum.humidity)
                        : 'N/A';
                });

                this.data.forEach(item => {
                    item.favorite = favorites.some(f => f.device_id === item.device_id);
                });
                this.loading = false;
            } catch (error) {
                this.loading = false;
                console.error('Error fetching data:', error);
                this.setMessage(this.$t('error') + ': ' + error.message, false);
            }
        },
        openChildTable(event) {
            const target = event.target;
            // Handle expand/collapse
            if (target?.classList.contains('dt-control')) {
                const row = this.$refs.datatable.dt.row(target.closest('tr'));
                if (row.child.isShown()) {
                    row.child.hide();
                } else {
                    row.child(this.childRows(row.data())).show();
                }
                return;
            }

            // Handle favorite button
            if (target?.classList.contains('edit-favorite')) {
                const tr = target.closest('tr');
                const row = this.$refs.datatable.dt.row(tr);
                const rowData = row.data();
                this.editFavorite(rowData);
                return;
            }

            // Handle temp/hum/activity buttons in child row
            if (target?.classList.contains('popup-btn')) {
                const type = target.dataset.type;
                const device = target.dataset.device;
                this.openPopUp(type, device);
                return;
            }
        },
        childRows(row) {
            return `
                <div class="row">
                    <div class="col-2">
                        <button class="btn btn-primary btn-sm popup-btn" data-type="temp_graph" data-device="${row.device_name}">
                            ${this.$t('dataheaders.temp_graph')}
                        </button>
                    </div>
                    <div class="col-2">
                        <button class="btn btn-primary btn-sm popup-btn" data-type="hum_graph" data-device="${row.device_name}">
                            ${this.$t('dataheaders.hum_graph')}
                        </button>
                    </div>
                    <div class="col-2">
                        <button class="btn btn-primary btn-sm popup-btn" data-type="activitylog" data-device="${row.device_name}">
                            ${this.$t('dataheaders.activitylog')}
                        </button>
                    </div>
                    <div class="col">
                        <h6>${this.$t('dataheaders.removed_time')}</h6>
                        <span>${formattedTime(row.removed_time)}</span>
                    </div>
                    ${this.isAdmin ?
                    `<div class="col">
                            <h6>${this.$t('dataheaders.assignees')}</h6>
                            ${row.assignees.length === 0 ?
                        `<span class="text-danger">${this.$t('missing_assignee')}</span>`
                        :
                        `<span>${row.assignees.join(', ')}</span>`
                    }
                        </div>`
                    :
                    ''
                }
                </div>`;
        },
        renderColumn(data, type, row, column) {
            switch (column) {
                case 'favorite':
                    // For sorting, return numeric value (1 = favorite, 0 = not)
                    if (type === 'sort') {
                        return row.favorite ? 1 : 0;
                    }
                    return `<button class="btn p-0 edit-favorite" data-device-id="${row.device_id}">
                            ${row.favorite ? '<i class="fas fa-star text-warning edit-favorite"></i>' : '<i class="far fa-star edit-favorite"></i>'}
                            </button>`;
                case 'last_online':
                    return data ? formattedTime(data) : 'N/A';
                case 'removed':
                    return this.renderBooleanData(data, type, true);
                case 'smoke_sensor':
                case 'temp_sensor':
                    return this.renderBooleanData(data, type, false);
                case 'temperature':
                    return this.renderNumericData(data, '°C');
                case 'humidity':
                    return this.renderNumericData(data, '%');
                case 'battery':
                    return this.renderBatteryData(data, type, row);
                case 'additional_info':
                    if (this.isAdmin && row.assignees && row.assignees.length === 0) {
                        return `<button class="accordion-button bg-transparent text-danger" type="button">!</button>`;
                    }
                    return `<button class="accordion-button bg-transparent" type="button"></button>`;
                default:
                    return data;
            }
        },
        renderNumericData(data, unit) {
            if (data == null || data === 'N/A') {
                return 'N/A';
            }
            return `${Number.parseFloat(data).toFixed(1)} ${unit}`;
        },
        renderBooleanData(data, type, isReversed) {
            // For sorting, return numeric value (1 = true, 0 = false)
            if (type === 'sort') {
                return typeof data === 'boolean' ? (data ? 1 : 0) : null;
            }
            if (typeof data === 'boolean') {
                const isTrue = isReversed ? !data : data;
                return `<i class="${isTrue ? 'fas fa-check-circle text-success' : 'fas fa-times-circle text-danger'}"></i>`;
            } else {
                return '';
            }
        },
        renderBatteryData(data, type, row) {
            if (type === 'sort') {
                return data == null ? -1 : data; // Ensure nulls are sorted at the end
            }
            if (data == null) {
                return 'N/A';
            }
            const isOrigin = row.device_name.startsWith('ORIGIN');
            const unit = isOrigin ? this.$t('mo') : 'mV';
            return `${data} ${unit}`;
        },
        getGroupName(groupId) {
            const group = this.groups.find(group => group.group_id === groupId);
            return group ? group.group_name : '';
        },
        getGroupAddress(groupId) {
            const group = this.groups.find(group => group.group_id === groupId);
            return group ? group.address : '';
        },
        clearPopups() {
            this.showTempGraph = false;
            this.showHumGraph = false;
            this.showEventLog = false;
        },
        openPopUp(type, device) {
            this.clearPopups();
            this.devicename = device;
            if (type === 'temp_graph') {
                this.showTempGraph = true;
            } else if (type === 'activitylog') {
                this.showEventLog = true;
            } else if (type === 'hum_graph') {
                this.showHumGraph = true;
            }
        },
        setMessage(message, isSuccess) {
            this.message = message;
            this.alertClass = isSuccess ? 'alert alert-success' : 'alert alert-warning';
        },
        async editFavorite(item) {
            console.log('editFavorite', item);
            if (item.favorite) {
                try {
                    const response = await fetch(`${this.$url}/favorites`, {
                        method: 'DELETE',
                        headers: {
                            Authorization: 'Bearer ' + getCookie('token'),
                            'Content-Type': 'application/json'
                        },
                        body: JSON.stringify({
                            device_id: item.device_id
                        })
                    });

                    if (response.ok) {
                        item.favorite = false;
                        // Force DataTables to redraw and update the cell (use draw() so ordering is applied)
                        this.$refs.datatable.dt.clear().rows.add(this.transformedData).draw();
                    } else {
                        console.log('Error deleting favorite:', response);
                        this.setMessage(this.$t('error') + ': ' + response.status, false);
                    }
                    // set timeout to clear message after 10 seconds
                    setTimeout(() => {
                        this.message = '';
                    }, 10000);
                } catch (error) {
                    console.error('Error deleting favorite:', error);
                    if (error.response && error.response.data) {
                        this.setMessage(this.$t('error') + ': ' + error.response.data, false);
                    } else {
                        this.setMessage(this.$t('error') + ': ' + error.message, false);
                    }
                    // set timeout to clear message after 10 seconds
                    setTimeout(() => {
                        this.message = '';
                    }, 10000);
                }
            } else {
                try {
                    const response = await fetch(`${this.$url}/favorites`, {
                        method: 'POST',
                        headers: {
                            Authorization: 'Bearer ' + getCookie('token'),
                            'Content-Type': 'application/json'
                        },
                        body: JSON.stringify({
                            device_id: item.device_id
                        })
                    });
                    if (response.ok) {
                        item.favorite = true;
                        // Force DataTables to redraw and update the cell (use draw() so ordering is applied)
                        this.$refs.datatable.dt.clear().rows.add(this.transformedData).draw();
                    } else {
                        console.log('Error deleting favorite:', response);
                        this.setMessage(this.$t('error') + ': ' + response.status, false);
                    }
                    // set timeout to clear message after 10 seconds
                    setTimeout(() => {
                        this.message = '';
                    }, 10000);
                } catch (error) {
                    console.error('Error adding favorite:', error);
                    if (error.response && error.response.data) {
                        this.setMessage(this.$t('error') + ': ' + error.response.data, false);
                    } else {
                        this.setMessage(this.$t('error') + ': ' + error.message, false);
                    }
                    // set timeout to clear message after 10 seconds
                    setTimeout(() => {
                        this.message = '';
                    }, 10000);
                }
            }
        },
        formattedTime
    }
};
</script>

<style scoped>
@import 'datatables.net-bs5/css/dataTables.bootstrap5.min.css';

.missing-assignee-btn {
    background-color: #b30000 !important;
    /* light red */
    color: #b30000 !important;
    /* dark red text */
    border-color: #b30000 !important;
}

/* Prevent horizontal scrollbar on large viewports by allowing the table to fit */
@media (min-width: 992px) {
    :deep(.table-responsive) {
        overflow-x: visible !important;
    }
}

/* Keep table body cells trimmed with ellipsis to avoid expanding rows */
:deep(table.dataTable tbody td) {
    white-space: nowrap !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
}

/* Allow headers to wrap so they don't force table wider */
:deep(table.dataTable thead th .dt-column-header) {
    display: flex !important;
    align-items: center !important;
    gap: 0.5rem !important;
    flex-wrap: wrap !important;
    white-space: normal !important;
}

/* Mobile-friendly overrides: relax tight compacting for small screens */
@media (max-width: 576px) {
    :deep(.table-responsive) {
        width: 100% !important;
        max-width: 100% !important;
        margin-left: 0 !important;
        margin-right: 0 !important;
        overflow-x: auto !important;
        /* allow horizontal scrolling when needed */
        -webkit-overflow-scrolling: touch;
    }

    :deep(table.dataTable) {
        table-layout: auto !important;
        /* allow natural column widths */
        font-size: 0.98rem !important;
        /* slightly larger for readability */
    }

    :deep(table.dataTable tbody td) {
        white-space: normal !important;
        /* let body cells wrap on small screens */
        word-break: break-word !important;
    }

    :deep(.dataTables_wrapper) {
        width: 100% !important;
        font-size: 0.98rem !important;
    }

    /* Stack the top controls vertically and give them breathing room */
    :deep(.dataTables_wrapper .dt-top) {
        display: flex !important;
        flex-direction: column !important;
        gap: 0.5rem !important;
        align-items: stretch !important;
    }

    :deep(.dataTables_wrapper .dataTables_length),
    :deep(.dataTables_wrapper .dataTables_filter) {
        float: none !important;
        width: 100% !important;
        display: flex !important;
        justify-content: space-between !important;
        align-items: center !important;
        gap: 0.5rem !important;
    }

    :deep(.dataTables_wrapper .dataTables_info),
    :deep(.dataTables_wrapper .dataTables_paginate) {
        float: none !important;
        width: 100% !important;
        display: flex !important;
        justify-content: space-between !important;
        align-items: center !important;
        margin-top: 0.5rem !important;
    }

    :deep(.dataTables_wrapper .pagination) {
        justify-content: flex-end !important;
    }

    /* Slightly increase control sizes on mobile for touch targets */
    :deep(.dataTables_wrapper select),
    :deep(.dataTables_wrapper .dataTables_filter input),
    :deep(.dataTables_wrapper .page-link) {
        font-size: 1rem !important;
        padding: 0.35rem 0.6rem !important;
    }
}
</style>
